// 작전 구성 화면의 상단 탭 바, 세 탭이 처음에는 전부 빈 슬롯이었다
//
// **왜 이 파일이 T1 에 있는가.** 세 탭을 채우는 스텝이 셋으로 갈려 있다. roster 는
// 명부 스텝, attack 은 작전 구성 스텝, defense 는 수비 스텝이다. 파일을 처음
// 만드는 일까지 그 셋에게 맡기면 누가 만들지가 정해지지 않고, 병렬로 돌 때 세 갈래가
// 같은 파일을 동시에 만들려 든다. 그래서 뿌리 스텝이 빈 채로 세워 둔다.
//
// **각 스텝은 자기 줄의 render 만 채운다. 남의 줄을 건드리지 않는다.**
//
// 아직 아무 슬롯도 안 찼을 때 탭을 눌러도 아무 일이 없는 대신 「어느 스텝이 채운다」를
// 화면에 적는다. 조용히 빈 화면을 내면 그것이 미구현인지 고장인지 구별되지 않는다.
//
// 탭은 원래 넷이었다. 2026-08-27 사용자 결정으로 rally 탭이 attack 탭에 흡수됐다. 두 탭이
// 서로를 「저쪽 탭에서」로 가리키던 것이 결함이었고, 그 사연은 `attack/index.tsx` 머리에 있다.

import type { ReactElement } from 'react'

import { compose as composeStrings } from '../../../../i18n/index.ts'
import { useLang } from '../../../shell/use-lang.ts'
import { AttackView } from '../attack/index.tsx'
import { DefenseView } from '../defense/index.tsx'
import { RosterView } from '../roster/index.tsx'
import { Icon } from '../../../ui/icons.tsx'

export type ComposeTabId = 'roster' | 'attack' | 'defense'

export interface ComposeTabSlot {
  id: ComposeTabId
  /** 탭에 적히는 말. 게임 용어라 번역하지 않는다. */
  label: string
  /** 그 스텝이 채우기 전에는 null 이다. */
  render: (() => ReactElement) | null
  /**
   * 이 슬롯을 채우는 스텝. **화면에 그대로 뜨므로 사람이 읽는 이름을 적는다.**
   *
   * 스텝 ID(`T5` 같은 것)를 여기 적지 않는다. 이 제품이 CT 에게 한 번도 정의한 적 없는
   * 이름이라, 그것을 본 CT 는 무엇을 눌러야 하는지 묻게 된다. ID 는 각 줄 위의 주석에
   * 있고 그것이 개발자가 읽는 자리다. (2026-08-11 사람 관점 채점 지적.)
   */
  owner: string
}

export const composeTabSlots: ComposeTabSlot[] = [
  // T4 roster 명부 — 아군·적군 roster 와 marching 갱신.
  { id: 'roster', label: 'roster', render: () => <RosterView />, owner: 'roster 명부' },

  // T5 작전 구성 attack, rally 편집기 하나(2열: 좌 폼, 우 시퀀스 목록)에 조립 마치기와
  // time handle 과 confirm 과 내보낸 뒤의 retime 까지 전부 있다. 2026-08-27 에 rally 탭이
  // 이 탭에 흡수됐다.
  { id: 'attack', label: 'attack', render: () => <AttackView />, owner: '작전 구성 attack' },

  // T8 수비 — 적 rally 등록과 재앵커와 삭제.
  { id: 'defense', label: 'defense', render: () => <DefenseView />, owner: '수비' },
]

export interface ComposeTabBarProps {
  active: ComposeTabId
  onSelect: (id: ComposeTabId) => void
  /** 지금 보이는 「다음:」 줄이 가리키는 탭. 그 탭이 지금 탭이 아니면 테두리로 강조한다(`cue.ts`). */
  cue?: ComposeTabId | null
}

export function ComposeTabBar(props: ComposeTabBarProps): React.ReactElement {
  // 낭독기가 읽는 이름도 화면 언어를 따른다. 2026-08-26 독립 채점자가 하드코딩된 「작전 구성」을 찾았다.
  const t = composeStrings(useLang().lang)
  return (
    <nav
      data-testid="compose-tabbar"
      aria-label={t.title}
      className="flex gap-1 border-b border-line-hair"
    >
      {composeTabSlots.map((slot) => {
        const active = slot.id === props.active
        const cued = !active && slot.id === props.cue
        return (
        <button
          key={slot.id}
          type="button"
          role="tab"
          aria-selected={active}
          data-testid={`compose-tab-${slot.id}`}
          data-filled={String(slot.render !== null)}
          data-cue={String(cued)}
          // **셋이 폭을 나눠 갖고, 좁아지면 좌우 패딩이 먼저 양보한다.** `flex` 항목의
          // 기본 `min-width: auto` 는 글자보다 작아지지 못해서, 탭이 넷이던 시절 340픽셀에서
          // 네 탭의 합이 341이 되어 문서가 1픽셀 가로로 밀렸다. 정본이 「네 폭에서 가로
          // 스크롤 없이」를 못박은 그 항목이다. 예전에는 16픽셀이었고 줄어든 잔량이 이것이다.
          // 탭이 셋이 된 뒤에도 그 규칙은 그대로 둔다. 글자가 길어지면 같은 일이 다시 생긴다.
          //
          // **글자를 줄여 맞추지 않는다.** 그것은 340에서만 맞고 다른 폭에서 작아지는
          // 땜질이며, 글자를 키우는 쪽으로 가는 이 저장소의 방향과 정면으로 부딪힌다.
          // 「다음:」 줄이 가리키는 탭은 지금 탭의 밑줄과 같은 색 테두리를 두르고 주 잉크로 선다.
          // 지금 탭(밑줄)과 모양을 갈라 「여기 있다」와 「다음은 저기」가 섞이지 않게 한다.
          className={`tab min-w-0 flex-1 gap-1.5 px-2 uppercase sm:gap-2 sm:px-3 ${
            active ? 'tab-on' : cued ? 'tab-cue' : ''
          }`}
          onClick={() => {
            props.onSelect(slot.id)
          }}
        >
          <Icon name={slot.id === 'roster' ? 'roster' : slot.id === 'attack' ? 'attack' : 'defense'} />
          {slot.label}
        </button>
        )
      })}
    </nav>
  )
}

/** 아직 안 찬 탭을 눌렀을 때 뜨는 것. 침묵하지 않는 것이 요점이다. */
export function ComposeTabPanel(props: { active: ComposeTabId }): React.ReactElement {
  const slot = composeTabSlots.find((candidate) => candidate.id === props.active)
  if (slot === undefined) {
    throw new Error(`알 수 없는 탭이다: ${props.active}`)
  }
  if (slot.render !== null) return slot.render()

  // 말투를 제품과 맞춘다. 이 문장은 CT 가 읽는 글자이고, 같은 저장소의 입장 화면이
  // 이미 「카운트다운 화면은 아직 서지 않았습니다」라는 본보기를 갖고 있다.
  return (
    <p data-testid="compose-tab-empty" className="py-block text-sm text-ink-dim">
      {slot.label} 탭은 아직 서지 않았습니다 — {slot.owner} 를 만드는 단계가 이 자리를
      채웁니다.
    </p>
  )
}
